import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { LayoutDashboard, Users as UsersIcon, PlusSquare, Database, PieChart, FileBarChart, ShieldCheck, LogOut, UserCircle2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/AuthContext";

import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import TradeEntry from "@/pages/TradeEntry";
import StockMaster from "@/pages/StockMaster";
import ShareholdingReport from "@/pages/ShareholdingReport";
import TradingHistoryReport from "@/pages/TradingHistoryReport";
import Users from "@/pages/Users";
import Login from "@/pages/Login";

const BASE_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-tab-dashboard" },
  { to: "/clients", label: "Clients", icon: UsersIcon, testid: "nav-tab-clients" },
  { to: "/trade-entry", label: "Trade Entry", icon: PlusSquare, testid: "nav-tab-trade-entry" },
  { to: "/stocks", label: "Stock Master", icon: Database, testid: "nav-tab-stock-master" },
  { to: "/shareholding", label: "Shareholding", icon: PieChart, testid: "nav-tab-shareholding-report" },
  { to: "/trading-history", label: "History Report", icon: FileBarChart, testid: "nav-tab-trading-history" },
];

function Sidebar() {
  const { user, logout, isAdmin } = useAuth();
  const nav = useNavigate();
  const NAV = [...BASE_NAV, ...(isAdmin ? [
    { to: "/users", label: "User Access", icon: ShieldCheck, testid: "nav-tab-users" },
  ] : [])];

  return (
    <aside className="md:w-64 md:min-h-screen border-b md:border-b-0 md:border-r border-slate-800/80 bg-[#0B111C]/90 backdrop-blur-md flex flex-col">
      <div className="p-6 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
          <span className="font-heading font-bold text-emerald-400 text-lg">W</span>
        </div>
        <div>
          <div className="font-heading font-extrabold text-lg leading-none">Watch Tower</div>
          <div className="label-caps mt-1">Trade Terminal</div>
        </div>
      </div>

      <nav className="px-3 pb-2 flex md:flex-col gap-1 overflow-x-auto flex-1">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            className={({ isActive }) =>
              `nav-item ${isActive ? "active bg-slate-800/60 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800/40"} flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium whitespace-nowrap`
            }
          >
            <item.icon size={18} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="p-3 md:border-t border-slate-800/60">
        <div className="flex items-center gap-2 p-2 rounded-lg bg-slate-900/50">
          <div className="w-8 h-8 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center">
            {isAdmin ? <ShieldCheck size={14} className="text-amber-400" /> : <UserCircle2 size={14} className="text-slate-400" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate" data-testid="sidebar-current-user">{user?.username}</div>
            <div className="text-[10px] label-caps !text-slate-500">{user?.role}</div>
          </div>
          <button onClick={() => { logout(); nav("/login"); }} data-testid="btn-logout" className="p-1.5 rounded-md text-slate-400 hover:text-rose-400 hover:bg-slate-800/60" title="Sign out">
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen tactical-grid">
      <div className="flex flex-col md:flex-row min-h-screen">
        <Sidebar />
        <main className="flex-1 min-w-0">
          <div className="max-w-[1600px] mx-auto p-4 md:p-8">{children}</div>
        </main>
      </div>
    </div>
  );
}

function Protected({ children, adminOnly = false }) {
  const { user, isAdmin } = useAuth();
  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/dashboard" replace />;
  return <Shell>{children}</Shell>;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster theme="dark" position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/clients" element={<Protected><Clients /></Protected>} />
          <Route path="/clients/:id" element={<Protected><ClientDetail /></Protected>} />
          <Route path="/trade-entry" element={<Protected><TradeEntry /></Protected>} />
          <Route path="/stocks" element={<Protected><StockMaster /></Protected>} />
          <Route path="/shareholding" element={<Protected><ShareholdingReport /></Protected>} />
          <Route path="/trading-history" element={<Protected><TradingHistoryReport /></Protected>} />
          <Route path="/users" element={<Protected adminOnly><Users /></Protected>} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
